import React from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import Groups2Icon from "@mui/icons-material/Groups2";
import PersonIcon from "@mui/icons-material/Person";
import NotesIcon from "@mui/icons-material/Notes";
import {
    formatoEuroEvento,
    formatoFecha,
    getEventType,
    getPrecioTicketEvento,
    getResumenCategoriasTickets,
    getTextoEncargadosEvento,
} from "../eventos/eventosUtils";

const contarAsistentes = (evento) => (
    (evento?.asistentes || []).reduce((acc, asistente) => {
        const tickets = Array.isArray(asistente.tickets) ? asistente.tickets : [];
        const invitados = asistente.invitados || {};
        const legacyInvitados = Number(invitados.invitados || 0)
            + Number(invitados.invitados_nino1 || invitados["invitados_niño1"] || 0)
            + Number(invitados.invitados_nino2 || invitados["invitados_niño2"] || 0);

        return acc + (Number(asistente.miembro_asiste ?? 1) === 1 ? 1 : 0)
            + (tickets.length ? tickets.reduce((sum, ticket) => sum + Number(ticket.cantidad || 0), 0) : legacyInvitados);
    }, 0)
);

const getAsistenteNombre = (asistente) => asistente.nombre || asistente.label || "Sense nom";

const InfoRow = ({ icon, label, value }) => (
    <Box className="event-dialog-info-row">
        <Box className="event-dialog-info-icon">{icon}</Box>
        <Box>
            <Typography className="event-dialog-info-label">{label}</Typography>
            <Typography className="event-dialog-info-value">{value || "Sense assignar"}</Typography>
        </Box>
    </Box>
);

export const EventoDialog = ({ open, setOpen, tarea, opTrabajadores = [] }) => {
    if (!tarea) return null;

    const tipo = getEventType(tarea.id_tipo_evento);
    const TipoIcon = tipo.icon;
    const encargados = getTextoEncargadosEvento(tarea, opTrabajadores);
    const asistentes = tarea.asistentes || [];
    const totalAsistentes = contarAsistentes(tarea);
    const resumenCategorias = getResumenCategoriasTickets(tarea);
    const esMenjar = Number(tarea.id_tipo_evento) === 1;
    const esRoba = Number(tarea.id_tipo_evento) === 4;
    const descripcionVisible = tarea.descripcion && tarea.descripcion !== tarea.asunto;
    const preciosTickets = (tarea.tipos_tickets || []).filter((tipoTicket) => Number(tipoTicket.paga) === 1);

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            onClick={(event) => event.stopPropagation()}
            fullWidth
            maxWidth="sm"
            PaperProps={{ className: `event-dialog-paper ${tipo.className}` }}
        >
            <DialogTitle className="event-dialog-title">
                <Box className="event-dialog-type-icon">
                    <TipoIcon />
                </Box>
                <Box className="event-dialog-title-copy">
                    <Typography className="event-dialog-eyebrow">{tipo.label}</Typography>
                    <Typography variant="h5" className="event-dialog-heading">
                        {tarea.asunto || tarea.descripcion || tipo.label}
                        {esRoba && tarea.color_ropa && (
                            <span className="event-dialog-color-dot" style={{ backgroundColor: tarea.color_ropa }} />
                        )}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers className="event-dialog-content">
                <Stack spacing={1.5}>
                    <Box className="event-dialog-hero">
                        <InfoRow
                            icon={<AccessTimeIcon fontSize="small" />}
                            label="Data i hora"
                            value={`${formatoFecha(tarea.fecha)} · ${tarea.hora_inicio || "--:--"}${tarea.hora_final ? ` - ${tarea.hora_final}` : ""}`}
                        />
                        {!esRoba && (
                            <InfoRow
                                icon={<PersonIcon fontSize="small" />}
                                label={encargados.label}
                                value={encargados.texto}
                            />
                        )}
                    </Box>

                    {descripcionVisible && (
                        <Box className="event-dialog-section">
                            <Box className="event-dialog-section-title">
                                <NotesIcon fontSize="small" />
                                <Typography>Descripció</Typography>
                            </Box>
                            <Typography className="event-dialog-description">{tarea.descripcion}</Typography>
                        </Box>
                    )}

                    {(tarea.id_apartado || tarea.id_concepto_contabilidad || Number(tarea.miembro_paga) === 1 || esMenjar) && (
                        <Box className="event-dialog-section">
                            <Box className="event-dialog-section-title">
                                <AccountBalanceWalletIcon fontSize="small" />
                                <Typography>Comptabilitat</Typography>
                            </Box>
                            <Box className="event-dialog-metrics">
                                <Box>
                                    <span>Concepte comptable</span>
                                    <strong>{tarea.concepto_contable || (tarea.id_apartado ? `#${tarea.id_apartado}` : "Sense assignar")}</strong>
                                </Box>
                                <Box>
                                    <span>Concepte predefinit</span>
                                    <strong>{tarea.nombre_concepto_contabilidad || (tarea.id_concepto_contabilidad ? `#${tarea.id_concepto_contabilidad}` : "No definit")}</strong>
                                </Box>
                                <Box>
                                    <span>El membre paga</span>
                                    <strong>{Number(tarea.miembro_paga) === 1 ? formatoEuroEvento(tarea.precio_miembro) : "No"}</strong>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {esMenjar && preciosTickets.length > 0 && (
                        <Box className="event-dialog-section">
                            <Box className="event-dialog-section-title">
                                <ConfirmationNumberIcon fontSize="small" />
                                <Typography>Preus per tiquet</Typography>
                            </Box>
                            <Box className="event-dialog-ticket-prices">
                                {preciosTickets.map((tipoTicket) => (
                                    <Box key={tipoTicket.id}>
                                        <span>{tipoTicket.nombre}</span>
                                        <strong>{formatoEuroEvento(getPrecioTicketEvento(tarea, tipoTicket))}</strong>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {!esRoba && (
                        <Box className="event-dialog-section">
                            <Box className="event-dialog-section-title">
                                <Groups2Icon fontSize="small" />
                                <Typography>Assistència</Typography>
                                <Chip size="small" label={`${totalAsistentes} total`} />
                            </Box>
                            {resumenCategorias.length > 0 && (
                                <Box className="event-dialog-ticket-summary">
                                    {resumenCategorias.map((categoria) => (
                                        <Chip key={categoria.id} size="small" label={`${categoria.nombre}: ${categoria.cantidad}`} />
                                    ))}
                                </Box>
                            )}
                            <Stack className="event-dialog-attendees">
                                {asistentes.length > 0 ? asistentes.map((asistente) => (
                                    <Box key={asistente.id_usuario || getAsistenteNombre(asistente)} className="event-dialog-attendee">
                                        <Typography>{getAsistenteNombre(asistente)}</Typography>
                                        <span>{Number(asistente.miembro_asiste ?? 1) === 1 ? "Membre" : "No assisteix"}</span>
                                    </Box>
                                )) : (
                                    <Typography className="event-dialog-empty">Encara no hi ha assistents.</Typography>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions className="event-dialog-actions">
                <Button onClick={handleClose} variant="contained">
                    Tancar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
